import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { faHistory, faCheckCircle, faSync, faTimes, faExclamationCircle, faLock } from '@fortawesome/pro-light-svg-icons';
import { DateTime } from 'luxon';
import { AuthService } from '../../../auth/auth.service';
import { CaseService } from '../../../shared/services/case.service';
import { CreditSafecheckService } from '../../../shared/services/credit-safecheck.service';
import { LookupsService } from '../../../shared/services/lookups.service';
import { CreditSafeCheckHistoryComponent } from './credit-safe-check-history/credit-safe-check-history.component';
import { ManualCheckComponent } from './manual-check/manual-check.component';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'src/app/shared/services/modal.service';
import { YesNoPopupComponent } from 'src/app/shared/components/yes-no-popup/yes-no-popup.component';

@Component({
  selector: 'app-credit-safe-check',
  templateUrl: './credit-safe-check.component.html',
  styleUrls: ['./credit-safe-check.component.scss']
})
export class CreditSafeCheckComponent implements OnInit {

  @Input() caseId: number = 0;
  @Input() clientNo: number = 1;
  @Input() initialCreditCheckData: any = {};

  faHistory = faHistory;
  faCheckCircle = faCheckCircle;
  faExclamationCircle = faExclamationCircle;
  faSync = faSync;
  faTimes = faTimes;
  faLock = faLock;

  creditCheckData: any = {};
  
  titleList: any[] = [];
  showSpinner: boolean = false;
  dataLoaded: boolean = false;

  creditSafeCheck = this.fb.group({
    caseId: null,
    titleId: '',
    tite: '',
    firstName: '',
    otherNames: '',
    surname: '',
    dob: '',
    buildingNo: 0,
    buildingName: '',
    street: '',
    city: '',
    postCode: '',
    clientNo: 0
  }, {});

  constructor(
    public activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private caseService: CaseService,
    public modalService: ModalService,
    private creditSafecheckService: CreditSafecheckService,
    private lookupService: LookupsService,
    public authService: AuthService  ) { }

  ngOnInit(): void {

    this.loadTitles();
    this.loadCreditCheck();
    
    Object.keys(this.creditSafeCheck.controls).forEach(key => {
      this.creditSafeCheck.get(key)!.markAsTouched();
      this.creditSafeCheck.get(key)!.updateValueAndValidity();
    });
  }

  loadCreditCheck(refreshCase: boolean = false) {

    this.creditSafecheckService.getCreditSafecheckInfo(this.caseId, this.clientNo)
      .subscribe(data => {
        if (data != null) {
          this.creditCheckData = data;
          this.setForm();
          if (refreshCase) {
            this.caseService.refreshCase.next();
          }
        } else {
          // add if doesnt exist
          this.creditCheckData = this.initialCreditCheckData;
          this.setForm();
          this.creditSafecheckService.addCreditCheck(this.caseId, this.creditSafeCheck.value)
            .subscribe(data => {
              this.loadCreditCheck(refreshCase); // reload yourself :) 
            });
        }

        this.dataLoaded = true;
        
      });
  }

  loadTitles() {
    this.lookupService.getTitles()
      .subscribe(data => {
        this.titleList = data;
      });
  }

  runCreditSafecheck() {

  const modalRef = this.modalService.openWithOptions(YesNoPopupComponent, {size: 'lg'});
         
    modalRef.componentInstance.messageHeader = 'Are you sure you want to run AML check against this client?';
    modalRef.componentInstance.popupText1 = '';
    modalRef.componentInstance.popupText2 = '';

      modalRef.result.then(
        (result) => {
          if(result)
          {
            this.showSpinner = true;

            this.creditSafecheckService.runCreditSafecheck(this.caseId, this.creditSafeCheck.value)
              .subscribe(data => {
                this.showSpinner = false;
                this.loadCreditCheck(true);
              });
        }
    },
      () => {} // dismissed - do nothing
    );

    
  }

  runManualCreditSafecheck() {

    const modalRef = this.modalService.openWithOptions(ManualCheckComponent, {size: 'md'});
         
      modalRef.result.then(
        (result) => {
          if(result)
          {
              this.creditSafecheckService.addManualCreditSafecheck(this.caseId, this.clientNo, result)
              .subscribe(data => {
                if (data) {
                  this.loadCreditCheck();
                  this.caseService.refreshCase.next();
                }
              });
        }
    },
      () => {} // dismissed - do nothing
    );
  }

  unlock() {

    const modalRef = this.modalService.openWithOptions(YesNoPopupComponent, {size: 'lg'});
         
    modalRef.componentInstance.messageHeader = 'Are you sure you want to unlock this client?';
    modalRef.componentInstance.popupText1 = '';
    modalRef.componentInstance.popupText2 = '';

      modalRef.result.then(
        (result) => {
          if(result)
          {
            this.creditSafecheckService.unlockCreditSafecheck(this.caseId, this.clientNo)
          .subscribe(data => {
            if (data) {
              this.loadCreditCheck(true);
            }
          });
        }
    },
      () => {} // dismissed - do nothing
    );
   
  }

  refresh() {

    const modalRef = this.modalService.openWithOptions(YesNoPopupComponent, {size: 'lg'});
         
    modalRef.componentInstance.messageHeader = 'Are you sure you want to reset to the original client data?';
    modalRef.componentInstance.popupText1 = '';
    modalRef.componentInstance.popupText2 = '';

    modalRef.result.then(
        (result) => {
          if(result)
          {
            this.creditCheckData = this.initialCreditCheckData;
            this.setForm();
        }
    },
      () => {} // dismissed - do nothing
    );

   
  }

  updateField() {
    this.creditSafecheckService.editCreditCheck(this.caseId, this.creditSafeCheck.value)
      .subscribe(data => {
        
      });
  }

  openHistory() {

    const modalRef = this.modalService.openWithOptions(CreditSafeCheckHistoryComponent, {size: 'xl'});
         
    modalRef.componentInstance.caseId = this.caseId;
    modalRef.componentInstance.clientNo = this.clientNo
  }


  close() {
    this.activeModal.dismiss();
  }

  private setForm() {
    this.creditSafeCheck.controls["clientNo"].setValue(this.clientNo);

    this.creditSafeCheck.controls["titleId"].setValue(this.creditCheckData.titleId);
    this.creditSafeCheck.controls["firstName"].setValue(this.creditCheckData.firstName);
    this.creditSafeCheck.controls["otherNames"].setValue(this.creditCheckData.otherNames);
    this.creditSafeCheck.controls["surname"].setValue(this.creditCheckData.surname);
    this.creditSafeCheck.controls["buildingNo"].setValue(this.creditCheckData.buildingNo);
    this.creditSafeCheck.controls["buildingName"].setValue(this.creditCheckData.buildingName);
    this.creditSafeCheck.controls["street"].setValue(this.creditCheckData.street);
    this.creditSafeCheck.controls["city"].setValue(this.creditCheckData.city);
    this.creditSafeCheck.controls["postCode"].setValue(this.creditCheckData.postCode);
    
    if (this.creditCheckData.dateOfBirth) {
      const dt = DateTime.fromISO(this.creditCheckData.dateOfBirth);
      if (dt.isValid) {
        this.creditSafeCheck.controls['dob'].setValue(dt.toFormat('yyyy-MM-dd'));
      }
    }
  }

  onDateChange() {
    this.updateField();
  }

 
  formatDate(inDate: any) {
    var formattedDate = '';


    if (inDate !== null) {
      
      var dt = DateTime.fromISO(inDate);
     
      formattedDate = dt.toFormat("dd-MM-yyyy");

      if (formattedDate == 'Invalid date' || formattedDate == 'Invalid DateTime')
        formattedDate = '';

    }


    return formattedDate;

  }

}
